import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp,
  Warning,
  CheckCircle,
  ArrowRight,
  TrendUp,
  Brain,
  Target,
  Question,
  CaretDown,
  Clock,
  Lightning,
  ChartBar,
  Crosshair,
  Timer,
  Activity,
  ListChecks,
  Eye
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../../lib/seo';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const ComoMelhorarDayTrade = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'Como Melhorar no Day Trade | Guia Prático 2025',
      description: 'Aprenda como melhorar seus resultados no day trade com técnicas práticas baseadas em dados e análise de performance.',
      path: '/pt/como-melhorar-no-day-trade',
      lang: 'pt-BR',
      alternates: getArticleAlternates('/pt/como-melhorar-no-day-trade'),
    });
    
    return () => {
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(null);

  const faqs = [
    {
      question: "Quanto tempo leva para melhorar no day trade?",
      answer: "Depende da sua consistência em analisar dados. Com um diário de trades e revisões semanais, a maioria dos traders vê melhorias perceptíveis em 2-3 meses. Sem dados, você pode passar anos repetindo os mesmos erros. O segredo não é tempo, é feedback estruturado."
    },
    {
      question: "Devo operar todo dia para melhorar?",
      answer: "Não necessariamente. Qualidade supera quantidade. Um trader que opera 3x por semana com foco total geralmente evolui mais rápido que um que opera todo dia de forma mecânica. O importante é ter operações suficientes para gerar dados estatisticamente relevantes — geralmente 50-100 trades."
    },
    {
      question: "Como sei se estou melhorando de verdade?",
      answer: "Olhe para métricas além do P&L: sua disciplina está melhorando? Você está seguindo seu plano? Seu profit factor está aumentando? A evolução real aparece primeiro na qualidade das decisões, depois no resultado financeiro."
    },
    {
      question: "Vale a pena fazer backtesting?",
      answer: "Backtesting é útil para validar ideias, mas o forward testing (operar ao vivo com tamanho reduzido) é onde a melhoria real acontece. No backtest você não sente a pressão emocional. O ideal é combinar: backtest para filtrar setups ruins, forward test para validar os bons."
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Começar Grátis
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 pt-16 pb-20">
        
        <header className="mb-12">
          <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
            <TrendUp size={16} weight="fill" />
            <span>Day Trade</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
            Como Melhorar no Day Trade
          </h1>
          <p className="text-lg text-white/60">
            Técnicas práticas baseadas em dados reais
          </p>
        </header>

        <div className="bg-gradient-to-r from-[#00E676]/10 to-transparent border-l-2 border-[#00E676] p-6 rounded-r-lg mb-12">
          <p className="text-white/80 leading-relaxed">
            A maioria dos day traders tenta melhorar buscando novos indicadores, novos setups, novos mercados. 
            Mas a melhoria real vem de entender profundamente o que você já faz — e otimizar isso com dados. 
            Este guia mostra o processo prático.
          </p>
        </div>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Brain size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Problema da Melhoria Intuitiva</h2>
          </div>
          
          <p className="text-white/70 leading-relaxed mb-6">
            Quando você tenta melhorar baseado em intuição, enfrenta vieses cognitivos poderosos. 
            Você lembra dos trades que deram certo. Esquece os que deram errado. 
            Superestima sua disciplina. Subestima seus erros recorrentes.
          </p>

          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-red-400 mb-4">Armadilhas Comuns</h3>
            <ul className="space-y-3 text-white/70">
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Viés de confirmação</strong> — você só vê evidências que confirmam suas crenças</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Memória seletiva</strong> — trades vencedores são mais memoráveis que perdedores</span>
              </li>
              <li className="flex items-start gap-3">
                <Warning size={18} className="text-red-400 mt-1 flex-shrink-0" />
                <span><strong className="text-white">Ilusão de controle</strong> — você acha que estava no controle quando teve sorte</span>
              </li>
            </ul>
          </div>

          <p className="text-white/70 leading-relaxed">
            <strong className="text-white">Dados são o antídoto.</strong> Números não mentem, não esquecem, não têm vieses. 
            Um diário de trades transforma impressões subjetivas em fatos objetivos.
          </p>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <ListChecks size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">O Processo de Melhoria Baseado em Dados</h2>
          </div>

          <div className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">1</div>
                <h3 className="font-semibold text-white">Registre Tudo</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Cada trade deve ser documentado: entrada, saída, tamanho, setup usado, horário, condição do mercado. 
                Sem dados, não há análise. Sem análise, não há melhoria sistemática.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">2</div>
                <h3 className="font-semibold text-white">Identifique Padrões</h3>
              </div>
              <p className="text-white/70 leading-relaxed mb-4">
                Com dados suficientes (50-100 trades), padrões emergem naturalmente:
              </p>
              <ul className="space-y-2 text-white/60">
                <li>• Qual setup tem melhor win rate?</li>
                <li>• Em qual horário você performa melhor?</li>
                <li>• Quanto seu profit factor cai após 3 trades seguidos?</li>
                <li>• Qual ativo você realmente tem edge?</li>
              </ul>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">3</div>
                <h3 className="font-semibold text-white">Elimine o Que Não Funciona</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                A melhoria mais rápida não vem de adicionar coisas novas — vem de cortar o que não funciona. 
                Se um setup tem profit factor abaixo de 1.0 após 30 trades, pare de usar. 
                Se você perde dinheiro consistentemente após às 15h, pare de operar nesse horário.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">4</div>
                <h3 className="font-semibold text-white">Dobre o Que Funciona</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Identificou que seu setup de rompimento em BTC tem profit factor de 2.3? 
                Foque nele. Aumente o tamanho gradualmente. 
                Especialização geralmente supera diversificação no day trade.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#00E676] text-black font-bold flex items-center justify-center text-sm">5</div>
                <h3 className="font-semibold text-white">Revise Semanalmente</h3>
              </div>
              <p className="text-white/70 leading-relaxed">
                Reserve 30 minutos por semana para revisar seus dados. 
                Olhe para métricas, não para P&L. 
                Uma semana de lucro com decisões ruins é pior que uma semana de loss com decisões boas.
              </p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <ChartBar size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Métricas Que Importam</h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-[#00E676] mb-2">Profit Factor</h3>
              <p className="text-white/60 text-sm">Ganhos totais ÷ Perdas totais. Acima de 1.5 é bom, acima de 2.0 é excelente.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-[#00E676] mb-2">Win Rate por Setup</h3>
              <p className="text-white/60 text-sm">Não win rate geral — por setup específico. Isso revela onde está seu edge real.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-[#00E676] mb-2">Drawdown Máximo</h3>
              <p className="text-white/60 text-sm">A maior queda do seu capital. Mede seu risco real, não o teórico.</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-5">
              <h3 className="font-semibold text-[#00E676] mb-2">RR Médio Realizado</h3>
              <p className="text-white/60 text-sm">Não o RR planejado — o que você realmente consegue. Geralmente menor que o planejado.</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Warning size={20} className="text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold">Erros Comuns na Melhoria</h2>
          </div>

          <div className="space-y-4">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Mudar de estratégia muito rápido</h3>
              <p className="text-white/60 text-sm">Você precisa de pelo menos 30-50 trades para avaliar um setup. Trocar antes disso é ruído, não sinal.</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Focar apenas no resultado financeiro</h3>
              <p className="text-white/60 text-sm">Um mês lucrativo por sorte não significa que você melhorou. Olhe para qualidade das decisões.</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Ignorar o aspecto emocional</h3>
              <p className="text-white/60 text-sm">Day trade é pressão psicológica constante. Se você não trabalha a mente, a técnica não salva.</p>
            </div>
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-5">
              <h3 className="font-semibold text-amber-400 mb-2">Não ter limites de loss diário</h3>
              <p className="text-white/60 text-sm">Um dia ruim sem limite pode destruir um mês inteiro de trabalho.</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[#00E676]/10 flex items-center justify-center">
              <Question size={20} className="text-[#00E676]" />
            </div>
            <h2 className="text-2xl font-bold">Perguntas Frequentes</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-white/10 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                >
                  <span className="font-medium text-white pr-4">{faq.question}</span>
                  <CaretDown 
                    size={20} 
                    className={`text-white/60 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5 text-white/70 leading-relaxed border-t border-white/5 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/10 via-transparent to-transparent border border-[#00E676]/20 rounded-2xl p-8">
            <h2 className="text-2xl font-bold mb-4">Comece a Melhorar Com Dados</h2>
            <p className="text-white/70 mb-6 leading-relaxed">
              GENI transforma seus trades em insights acionáveis. 
              Veja exatamente onde você está ganhando e perdendo edge.
            </p>
            <Link 
              to="/signup"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#00E676] text-black font-semibold rounded-lg hover:bg-[#00E676]/90 transition-colors"
            >
              Começar Grátis
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </section>

        <section>
          <h2 className="text-xl font-bold mb-4">Artigos Relacionados</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link 
              to="/pt/profit-factor-trading"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Profit Factor no Trading
              </h3>
              <p className="text-sm text-white/50">Entenda a métrica mais importante de qualidade</p>
            </Link>
            <Link 
              to="/pt/win-rate-trading"
              className="p-4 bg-white/5 border border-white/10 rounded-xl hover:border-[#00E676]/50 transition-colors group"
            >
              <h3 className="font-medium text-white mb-1 group-hover:text-[#00E676] transition-colors">
                Win Rate no Trading
              </h3>
              <p className="text-sm text-white/50">Por que acertar mais nem sempre é melhor</p>
            </Link>
          </div>
        </section>

      </article>

      <footer className="border-t border-white/10 py-12">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL}
                alt="GENI" 
                className="h-8 w-auto object-contain"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/about" className="hover:text-white transition-colors">Sobre</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Termos</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacidade</Link>
            </div>
            <p className="text-sm text-white/30">© 2025 GENI</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ComoMelhorarDayTrade;
